"use client";

import { useState } from "react";
import type { ParsedAddress } from "@/components/inputs/address-types";
import type { ZipLocation } from "@/hooks/use-zip-geocoding";
import { POSTAL_CODE_FORMAT } from "@shared/formats/postal-code";
import { ALL_REGIONS, getCountryFromRegion } from "@shared/formats/state";

export interface ManualAddressDraft {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
}

const EMPTY_DRAFT: ManualAddressDraft = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
};

export function countryFor(state: string) {
  return getCountryFromRegion(state) ?? "US";
}

function postalCodeError(draft: ManualAddressDraft): string | undefined {
  const postalCode = draft.postalCode.trim();
  if (!postalCode) return undefined;
  if (countryFor(draft.state) === "CA") {
    const normalized = POSTAL_CODE_FORMAT.normalize(postalCode);
    return normalized && POSTAL_CODE_FORMAT.caPattern.test(normalized)
      ? undefined
      : "Enter a postal code like K1A 0B1.";
  }
  return POSTAL_CODE_FORMAT.pattern.test(postalCode) ||
    POSTAL_CODE_FORMAT.extendedPattern.test(postalCode)
    ? undefined
    : "Enter a 5-digit ZIP code.";
}

function zipMismatchError(
  draft: ManualAddressDraft,
  zipLocation: ZipLocation | null,
): string | undefined {
  if (!zipLocation || !draft.state || zipLocation.postalCode !== draft.postalCode.trim()) {
    return undefined;
  }
  if (zipLocation.state === draft.state) return undefined;
  return `ZIP ${zipLocation.postalCode} is in ${zipLocation.city}, ${zipLocation.state}. Check the state or ZIP.`;
}

export function toParsedAddress(draft: ManualAddressDraft): ParsedAddress {
  const state = draft.state.trim().toUpperCase();
  const country = countryFor(state);
  const zip =
    country === "CA"
      ? (POSTAL_CODE_FORMAT.normalize(draft.postalCode) ?? draft.postalCode.trim())
      : draft.postalCode.trim();
  const line2 = draft.line2.trim();
  return {
    line1: draft.line1.trim(),
    ...(line2 ? { line2 } : {}),
    city: draft.city.trim(),
    stateName: ALL_REGIONS.find((region) => region.code === state)?.name ?? state,
    state,
    zip,
    country,
  };
}

export function displayFor(address: ParsedAddress) {
  return [
    address.line1,
    address.line2,
    `${address.city}, ${address.state} ${address.zip}`,
  ]
    .filter(Boolean)
    .join(", ");
}

export function useManualAddressDraft() {
  const [draft, setDraft] = useState<ManualAddressDraft>(EMPTY_DRAFT);
  const [zipLocation, setZipLocation] = useState<ZipLocation | null>(null);
  const update = (patch: Partial<ManualAddressDraft>) =>
    setDraft((previous) => ({ ...previous, ...patch }));
  const onZipGeocode = (result: ZipLocation | null) => {
    setZipLocation(result);
    if (!result) return;
    setDraft((previous) => ({
      ...previous,
      city: previous.city.trim() ? previous.city : result.city,
      state: previous.state ? previous.state : result.state,
    }));
  };
  const zipError = postalCodeError(draft) ?? zipMismatchError(draft, zipLocation);
  const isComplete = Boolean(
    draft.line1.trim() &&
      draft.city.trim() &&
      draft.state.trim() &&
      draft.postalCode.trim(),
  );
  return { draft, update, onZipGeocode, zipError, canSubmit: isComplete && !zipError };
}
